import { useEffect } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { z } from "zod";
import { AdminShell } from "@/features/admin/AdminShell";
import { OverviewPage } from "@/features/admin/pages/OverviewPage";
import { AdminMatchesPage } from "@/features/admin/pages/MatchesPage";
import { AdminLivePage } from "@/features/admin/pages/LivePage";
import { AdminPerformancePage } from "@/features/admin/pages/PerformancePage";
import { AdminPeoplePage } from "@/features/admin/pages/PeoplePage";
import { AdminTeamsPage } from "@/features/admin/pages/TeamsPage";
import { AdminSeasonsPage } from "@/features/admin/pages/SeasonsPage";
import { AdminUsersPage } from "@/features/admin/pages/UsersPage";
import { AdminSettingsPage } from "@/features/admin/pages/SettingsPage";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { VerifyEmailPage } from "@/features/auth/pages/VerifyEmailPage";
import { ResetPasswordPage } from "@/features/auth/pages/ResetPasswordPage";
import { AccountPage } from "@/features/account/pages/AccountPage";
import { WelcomeShell } from "@/features/onboarding/pages/WelcomeShell";
import { InviteAcceptPage } from "@/features/onboarding/pages/InviteAcceptPage";
import { CanvasDemo } from "@/features/canvas/CanvasDemo";
import { CoachReview } from "@/features/coach-review/CoachReview";
import { CoachShell } from "@/features/coach/CoachShell";
import { FeedbackShell } from "@/features/feedback/FeedbackShell";
import { GameShell } from "@/features/game/pages/GameShell";
import { LandingPage } from "@/features/landing/pages/LandingPage";
import { HubRedirect } from "@/features/landing/pages/HubRedirect";
import { NotFoundPage } from "@/features/landing/pages/NotFoundPage";
import { TeamsIndexPage } from "@/features/teams/pages/TeamsIndexPage";
import { TeamMatchesPage } from "@/features/teams/pages/TeamMatchesPage";
import { TeamLivePage } from "@/features/teams/pages/TeamLivePage";
import { PublicAppHeader } from "@/features/navigation/PublicAppHeader";
import {
  LOCKDOWN_MODE,
  isLockdownAllowedPath,
} from "@/features/navigation/lockdown";

/**
 * Tktic SPA router (code-based, NOT file-based).
 *
 * Code-based was chosen over file-based for the milestone-arc shape:
 *   - M4 ships a handful of routes (/, /canvas-demo, /coach/review).
 *     File-based router adds bundler config for marginal benefit at
 *     this scale.
 *   - Coach Review's `match_id` / `note_id` / `clip_id` query params
 *     get statically typed via a Zod `validateSearch` schema — params
 *     flow through TanStack Router's typed `useSearch()` hook with
 *     no string-parsing boilerplate.
 *   - Adding a new route is one `createRoute` call in this file; no
 *     convention to remember about folder structure.
 *
 * File-based migration is a clean drop-in later (the route IDs stay
 * stable, only the registration changes). Defer until we have 12+
 * routes and the file approach starts winning ergonomically.
 *
 * Replaces the M3 hash-router placeholder in `main.tsx`.
 */

function RootLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const locked = LOCKDOWN_MODE && !isLockdownAllowedPath(pathname);

  useEffect(() => {
    if (locked) {
      void navigate({ to: "/teams", replace: true });
    }
  }, [locked, navigate]);

  if (locked) return null;

  return (
    <>
      <PublicAppHeader />
      <Outlet />
    </>
  );
}

/**
 * `notFoundComponent` renders inside `RootLayout`'s `<Outlet>`, so a
 * 404 keeps the persistent `<PublicAppHeader>` and theme chrome — it's
 * a normal page, not a dead end. Any URL matching no registered route
 * lands here instead of TanStack Router's bare error boundary.
 */
const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
});

/** M12a — `/hub` role-based redirect (sign-in landing target). */
const hubRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hub",
  component: HubRedirect,
});

/** M12a — `/teams` public index of every public team. */
const teamsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/teams",
  component: TeamsIndexPage,
});

/** M12a — `/teams/$teamSlug` per-team match list. */
const teamMatchesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/teams/$teamSlug",
  component: TeamMatchesPage,
});

/** M12c — `/teams/$teamSlug/live` per-team live viewer. */
const teamLiveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/teams/$teamSlug/live",
  component: TeamLivePage,
});

/** M11a — public game-view route. The slot is optional; when
 * absent, the GameShell defaults to the match's first ready slot
 * (full → first_half → second_half). Anonymous — accessible without
 * sign-in.
 *
 * `from` is an optional team slug breadcrumb: when a viewer opens a
 * match from a team page (`/teams/$teamSlug`), the match card stamps
 * `?from=<teamSlug>` so the in-page "Back to Matches" link can return
 * them to that team's match list instead of the marketing landing.
 * Absent (season view / direct link) ⇒ the back link falls back to
 * `/`. `.catch(undefined)` keeps a malformed value harmless. */
const gameSearchSchema = z.object({
  from: z.string().optional().catch(undefined),
});

export type GameSearch = z.infer<typeof gameSearchSchema>;

const gameRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/match/$slug",
  validateSearch: (search: Record<string, unknown>) =>
    gameSearchSchema.parse(search),
  component: GameShell,
});

const gameSlotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/match/$slug/$slot",
  validateSearch: (search: Record<string, unknown>) =>
    gameSearchSchema.parse(search),
  component: GameShell,
});

/** Throwaway M3 canvas demo. Kept here so reviewers can still
 * exercise the M3 canvas primitives directly without going through
 * Coach Review. Slated for deletion in M6 (or whenever the demo
 * stops earning its keep). */
const canvasDemoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/canvas-demo",
  component: CanvasDemo,
});

/** M9a — `/login?next=` real-UI route. `next` is the same-origin
 * path to redirect to after sign-in; `sanitizeNextPath` (in
 * `LoginPage`) rejects external URLs and protocol-relative paths
 * as a defense-in-depth. `.catch(undefined)` so a malformed
 * `?next=` falls back to the user's role-based default rather
 * than crashing on the parse-error boundary. */
const loginSearchSchema = z.object({
  next: z.string().optional().catch(undefined),
});

export type LoginSearch = z.infer<typeof loginSearchSchema>;

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>) =>
    loginSearchSchema.parse(search),
  component: LoginPage,
});

/** M9a C1 — `/verify-email?token=` landing. Anonymous route; the
 * token is the auth. `.catch(undefined)` so a malformed token
 * query string falls through to the manual-paste form. */
const verifyEmailSearchSchema = z.object({
  token: z.string().optional().catch(undefined),
});

export type VerifyEmailSearch = z.infer<typeof verifyEmailSearchSchema>;

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  validateSearch: (search: Record<string, unknown>) =>
    verifyEmailSearchSchema.parse(search),
  component: VerifyEmailPage,
});

/** M9a C2 — `/reset-password?token=` landing. Same `?token=`
 * pattern. Anonymous route. */
const resetPasswordSearchSchema = z.object({
  token: z.string().optional().catch(undefined),
});

export type ResetPasswordSearch = z.infer<typeof resetPasswordSearchSchema>;

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>) =>
    resetPasswordSearchSchema.parse(search),
  component: ResetPasswordPage,
});

/** M9a B1 — `/me?tab=` account self-service. `.catch(undefined)`
 * so an unknown tab falls back to `profile`. */
const accountSearchSchema = z.object({
  tab: z
    .enum(["profile", "password", "email", "sessions"])
    .optional()
    .catch(undefined),
});

export type AccountSearch = z.infer<typeof accountSearchSchema>;

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/me",
  validateSearch: (search: Record<string, unknown>) =>
    accountSearchSchema.parse(search),
  component: AccountPage,
});

/** M9b D1 — `/welcome` onboarding wizard. No search params; the
 * wizard's step state lives entirely in component memory (matches
 * legacy — closing the browser mid-wizard does NOT resume). The
 * `tktic_welcome_skipped` localStorage flag is the only persisted
 * signal, read by the auto-redirect effect in `AdminShell`. */
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/welcome",
  component: WelcomeShell,
});

/** M9b E1 — `/invite/$token` invite-acceptance landing. Path
 * param carries the (opaque, single-use) invite token; the page
 * dispatches between a signed-in accept flow and an unauth
 * create-account flow. Anonymous route — the token IS the auth. */
const inviteAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: InviteAcceptPage,
});

/** Coach Review search-param schema. All three are optional — the
 * bundle endpoint defaults missing keys (see
 * `routers/coach_review.py`). When the user opens the page raw, all
 * are undefined and the bundle returns a fresh-mount payload. Note +
 * clip are mutually exclusive at the BACKEND boundary (server 400s),
 * but we don't reject at the router boundary so a deep-link with
 * both renders an error state rather than blowing up before render.
 *
 * `z.coerce.number()` turns the string `?note_id=12` into `number`
 * so React Hook Form and consumer hooks don't have to parse. */
const coachReviewSearchSchema = z.object({
  match_id: z.string().optional(),
  note_id: z.coerce.number().int().positive().optional(),
  clip_id: z.coerce.number().int().positive().optional(),
  // M6a creation-routing carry-in: a list page routes INTO Coach
  // Review with intent. `source` picks the authoring surface
  // (video → a video note/clip, tactical_board → an observation);
  // `player_id` preselects a player on the observation composer.
  // Both `.catch(undefined)` so a stale/malformed value falls back
  // to the default (video, no preselection) rather than throwing.
  source: z
    .enum(["video", "tactical_board"])
    .optional()
    .catch(undefined),
  player_id: z.string().optional().catch(undefined),
});

export type CoachReviewSearch = z.infer<typeof coachReviewSearchSchema>;

const coachReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coach/review",
  validateSearch: (search: Record<string, unknown>) =>
    coachReviewSearchSchema.parse(search),
  component: CoachReview,
});

/** Coach workspace shell search schema. `tab` selects the list page;
 * absent ⇒ Board (Phase 1 player-first landing). The Review tab is the separate
 * `/coach/review` route, so `"review"` is intentionally NOT in
 * this enum.
 *
 * `.catch(undefined)` makes an unknown / malformed `?tab=` value
 * (a stale bookmark, a typo, `?tab=review` aimed at the wrong
 * route) coerce to `undefined` ⇒ Board — `validateSearch` never
 * THROWS, so the shell renders a coherent surface instead of
 * TanStack Router's parse-error boundary. */
const coachShellSearchSchema = z.object({
  tab: z
    .enum([
      "board",
      "roster",
      "matches",
      "library",
      // Legacy note/clip/summary library deep links. Retired
      // `playlists` and `engagement` values intentionally fall
      // through `.catch(undefined)` below and land on Board.
      "notes",
      "clips",
      "summaries",
      "settings",
    ])
    .optional()
    .catch(undefined),
  library: z
    .enum(["notes", "observations", "clips", "recaps"])
    .optional()
    .catch(undefined),
  player_id: z.string().optional().catch(undefined),
  library_item_id: z.coerce.number().int().positive().optional().catch(undefined),
  context: z
    .enum([
      "need_help",
      "tried_this",
      "no_recent_feedback",
      "unreviewed_feedback",
      "stale_goal",
      "missing_recap",
    ])
    .optional()
    .catch(undefined),
});

export type CoachShellSearch = z.infer<typeof coachShellSearchSchema>;

const coachShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coach",
  validateSearch: (search: Record<string, unknown>) =>
    coachShellSearchSchema.parse(search),
  component: CoachShell,
});

/** `/feedback?tab=` — the player/guardian-facing shell.
 *
 * The enum accepts the four action tabs (PR 1 of the Feedback Player
 * UX redesign: `overview · review · goals · history`) AND the five
 * legacy object-type values (`playlists · notes · clips · summaries
 * · development`) so existing deep links and stale bookmarks never
 * hit TanStack Router's parse-error boundary. The shell normalizes
 * via `normalizeFeedbackTab` before render and does NOT rewrite the
 * URL — the legacy value stays in history.
 *
 * `.catch(undefined)` makes any other value coerce to `undefined` ⇒
 * Overview (the new default), so anonymous typos behave the same way
 * as the coach shell. */
const feedbackShellSearchSchema = z.object({
  tab: z
    .enum([
      // Action tabs (PR 1).
      "overview",
      "review",
      "goals",
      "history",
      // Legacy object tabs — alias-only, folded by the shell.
      "playlists",
      "notes",
      "clips",
      "summaries",
      "development",
    ])
    .optional()
    .catch(undefined),
  /** PR 2: when present, deep-link the Review tab to a specific
   * match summary — the Overview's "Read full recap →" / "Read
   * summary" actions pass this so the user lands on Review with
   * the Summaries filter active AND that summary inline-expanded.
   * Coerced to a number; malformed values fall back to `undefined`
   * so a hand-edited URL doesn't crash the parse-error boundary. */
  summary: z.coerce.number().int().positive().optional().catch(undefined),
});

export type FeedbackShellSearch = z.infer<typeof feedbackShellSearchSchema>;

const feedbackShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/feedback",
  validateSearch: (search: Record<string, unknown>) =>
    feedbackShellSearchSchema.parse(search),
  component: FeedbackShell,
});

/** M8a — `/admin` parent route. Owns the shell (header + status
 * strip + sidebar + outlet); sub-routes mount inside `<Outlet />`.
 *
 * The parent path is `/admin` and renders the shell. Bare /admin
 * hits the shell's default-landing redirect effect (overview /
 * people / matches per role). */
const adminShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminShell,
});

const adminOverviewRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "overview",
  component: OverviewPage,
});

const adminMatchesRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "matches",
  component: AdminMatchesPage,
});

const adminLiveRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "live",
  component: AdminLivePage,
});

const adminPerformanceRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "performance",
  component: AdminPerformancePage,
});

// M8b — Tenants group routes.
const adminPeopleRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "people",
  component: AdminPeoplePage,
});

/** M8b F1 — `/admin/teams?team=` keeps the selected team in the
 * URL so refresh / deep-link / back-nav restores the operator's
 * selection. `.catch(undefined)` so a stale/malformed value falls
 * back to the page's auto-pick rather than the parse-error
 * boundary. */
const adminTeamsSearchSchema = z.object({
  team: z.string().optional().catch(undefined),
});

export type AdminTeamsSearch = z.infer<typeof adminTeamsSearchSchema>;

const adminTeamsRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "teams",
  validateSearch: (search: Record<string, unknown>) =>
    adminTeamsSearchSchema.parse(search),
  component: AdminTeamsPage,
});

const adminSeasonsRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "seasons",
  component: AdminSeasonsPage,
});

// M8c — Platform group routes.
const adminUsersRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "users",
  component: AdminUsersPage,
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "settings",
  component: AdminSettingsPage,
});

// Legacy redirects (M8a F5). The shell's useEffect handles the
// `/admin/streams` → `/admin/live` and `/admin/system` →
// `/admin/performance` swap; these route stubs let the URL match
// without 404'ing during the redirect frame.
const adminStreamsRedirect = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "streams",
  component: () => null,
});

const adminSystemRedirect = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "system",
  component: () => null,
});

const adminRouteTree = adminShellRoute.addChildren([
  adminOverviewRoute,
  adminMatchesRoute,
  adminLiveRoute,
  adminPerformanceRoute,
  adminPeopleRoute,
  adminTeamsRoute,
  adminSeasonsRoute,
  adminUsersRoute,
  adminSettingsRoute,
  adminStreamsRedirect,
  adminSystemRedirect,
]);

const routeTree = rootRoute.addChildren([
  indexRoute,
  canvasDemoRoute,
  loginRoute,
  verifyEmailRoute,
  resetPasswordRoute,
  accountRoute,
  welcomeRoute,
  inviteAcceptRoute,
  // M11 public viewer surfaces — anonymous, no auth gate.
  gameRoute,
  gameSlotRoute,
  // M12a — multi-team navigation. The global `/live` route was
  // removed once per-team live (`/teams/$teamSlug/live`) shipped —
  // a single-stream `/live` has no meaning in a multi-team install.
  hubRoute,
  teamsIndexRoute,
  teamMatchesRoute,
  teamLiveRoute,
  coachReviewRoute,
  coachShellRoute,
  feedbackShellRoute,
  adminRouteTree,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: false,
});

/** TanStack Router type registration — gives `useNavigate`,
 * `useSearch`, `Link`, etc. full type inference. */
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
